/**
 * "While you were working" digest (DESIGN.md §5, plan PR-5).
 *
 * Today carries ONE quiet line counting what the assistant handled
 * since the mechanic's previous app session — the full log lives under
 * More → Activity (the home-stays-quiet rule). The count reads
 * `GET /api/eric/history` against a localStorage baseline stamp.
 *
 * The baseline is a module-scope session latch: the first `peek` in a
 * JS session returns the PREVIOUS session's stamp; `commit` then writes
 * now() as the next session's baseline. Both are idempotent within a
 * session, so StrictMode double-mounts and tab-nav round trips can't
 * burn the baseline early — the digest keeps one stable meaning per
 * app session ("since you last had the app open").
 */

export type DigestEntry = { created_at: string }

const LAST_SEEN_KEY = 'autoflo_today_last_seen'

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

let sessionBaseline: string | null | undefined
let sessionStamp: string | undefined

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * The previous session's baseline stamp (ISO) — or null when there is
 * none (first run, or storage unavailable), in which case the digest
 * stays hidden. Pure after the first call in a session.
 */
export function peekDigestBaseline(
  storage: StorageLike | null = defaultStorage(),
): string | null {
  if (sessionBaseline === undefined) {
    try {
      sessionBaseline = storage?.getItem(LAST_SEEN_KEY) ?? null
    } catch {
      // Storage blocked (private mode / permissions) — stay quiet.
      sessionBaseline = null
    }
  }
  return sessionBaseline
}

/**
 * Marks this session as the new baseline and returns the stamp — the
 * instant this session started, which is also the digest window's
 * upper bound (see countEntriesSince). Latched: idempotent under
 * StrictMode double-invocation, so it is safe in a state initializer.
 * The stamp latches even when the storage write fails — the window end
 * is a session fact, not a persistence fact.
 */
export function commitDigestStamp(
  storage: StorageLike | null = defaultStorage(),
): string {
  if (sessionStamp === undefined) {
    sessionStamp = new Date().toISOString()
    try {
      storage?.setItem(LAST_SEEN_KEY, sessionStamp)
    } catch {
      // Best-effort — a failed write only means a stale (larger) count
      // next session, never an error surface.
    }
  }
  return sessionStamp
}

/**
 * Entries inside the "while you were working" window:
 * baseline < created_at <= sessionStamp. The upper bound keeps the
 * mechanic's OWN current-session requests out of the count when the
 * history query refetches mid-session (window focus / staleTime).
 * Null baseline → 0 (first run stays quiet).
 */
export function countEntriesSince(
  entries: readonly DigestEntry[],
  baselineIso: string | null,
  untilIso: string,
): number {
  if (!baselineIso) return 0
  const baseline = Date.parse(baselineIso)
  const until = Date.parse(untilIso)
  if (Number.isNaN(baseline) || Number.isNaN(until)) return 0
  return entries.filter((e) => {
    const at = Date.parse(e.created_at)
    return !Number.isNaN(at) && at > baseline && at <= until
  }).length
}

/** Test seam — clears the module latch between cases. */
export function _resetDigestForTests(): void {
  sessionBaseline = undefined
  sessionStamp = undefined
}
